<template>
  <div>
    <el-row class="columns-step__search">
      <el-col :span="12">
        <el-input
          :value="searchText"
          prefix-icon="el-icon-search"
          clearable
          :placeholder="$t(`${I18N}.search.placeholder`)"
          :aria-label="$t(`${I18N}.search.label`)"
          @input="setSearchText"
        >
          <el-select
            slot="prepend"
            :value="searchField"
            :aria-label="$t(`${I18N}.search.field`)"
            class="columns-step__search-field"
            @change="setSearchField"
          >
            <el-option
              v-for="field in Object.values(SEARCH_FIELDS)"
              :key="field"
              :value="field"
              :label="$t(`${I18N}.search.fields.${field}`)"
            />
          </el-select>
        </el-input>
      </el-col>
    </el-row>

    <ColumnMappingsTable
      v-loading="isLoading || isSaving"
      :rows="pageRows"
      :column-changes="columnChanges"
      :unmapped-columns="unmappedColumns"
      :pending-keys="pendingKeys"
      :field-errors="fieldErrors"
      :server-errors="serverErrors"
      :disabled="isSaving"
      @change="handleChange"
      @action="handleAction"
    />

    <el-pagination
      v-if="total > PAGE_SIZE"
      layout="prev, pager, next"
      :total="total"
      :page-size="PAGE_SIZE"
      :current-page="page"
      class="columns-step__pagination"
      @current-change="page = $event"
    />

    <!-- The one action of the step, on the side of the dialog's actions. -->
    <el-row
      type="flex"
      justify="end"
      align="middle"
      class="columns-step__actions"
    >
      <span
        class="columns-step__secondary"
        aria-live="polite"
      >
        <template v-if="leftUnmapped.length > 0">
          <i
            class="el-icon-warning-outline"
            aria-hidden="true"
          />
          {{ $tc(`${I18N}.leftUnmapped`, leftUnmapped.length) }}
        </template>
      </span>
      <span
        class="columns-step__secondary"
        aria-live="polite"
      >
        {{ writeCount > 0 ? $tc(`${I18N}.pendingWrites`, writeCount) : '' }}
      </span>
      <el-button
        type="primary"
        :loading="isSaving"
        @click="proceed"
      >
        {{ $t(`${I18N}.proceed`) }}
      </el-button>
    </el-row>
  </div>
</template>

<script>
  import debounce from 'lodash.debounce';
  import { analysesDataApi, analysisImportMappingApi } from '@/api';
  import ColumnMappingsTable from './ColumnMappingsTable.vue';
  import { searchAnalyses } from './analysisSearch';
  import {
    COLUMN_STATUSES,
    COLUMNS_MODES,
    serverErrorMessage,
  } from './wizardSteps';
  import {
    MAX_DECIMALS,
    OPERATIONS,
    PAGE_SIZE,
    ROW_ACTIONS,
    SEARCH_FIELDS,
    arrangeRows,
    buildOperations,
    columnsLeftUnmapped,
    countWrites,
    draftRow,
    duplicateFields,
    fileRowsFilter,
    invalidRows,
    matchesSearch,
    otherRowsFilter,
    otherRowsWhere,
    pageWindow,
    pendingIgnoredKey,
    pendingWrites,
    rowErrors,
    savedKey,
    savedRow,
    sortByFileColumn,
  } from './columnMappings';

  const SEARCH_DELAY_MS = 300;

  // Step 2 (FE-4). The mappings, in the order of C44: the file columns to map,
  // the rows of the file columns, every other row (`all` mode only, C59).
  // Nothing is written before "Continua" (C46), which writes, reruns prepare
  // with the same input and emits `prepared` with the response. Emits `dirty`
  // while writes wait for "Continua".
  export default {
    name: 'ColumnsStep',
    components: { ColumnMappingsTable },
    props: {
      input: { type: Object, required: true },
      columns: { type: Array, required: true },
      mode: { type: String, required: true },
    },
    data() {
      const unmappedColumns = this.columns
        .filter(({ status }) => status === COLUMN_STATUSES.UNMAPPED)
        .map(({ sourceText }) => sourceText);
      return {
        I18N: 'analysesData.importValues.columns',
        PAGE_SIZE,
        SEARCH_FIELDS,
        unmappedColumns,
        // One row of its own per column to map: left as is, nothing is written.
        drafts: unmappedColumns.map((sourceColumn, index) =>
          draftRow(`draft-${index + 1}`, { sourceColumn })
        ),
        draftCount: unmappedColumns.length,
        fileRows: [],
        // The page window of group 3 depends on the size of groups 1 and 2.
        hasFileRows: false,
        otherRows: [],
        otherCount: 0,
        // Saved rows being edited: `{[id]: {saved, row}}`.
        edits: {},
        // Saved rows to deactivate.
        removed: [],
        // Columns switching between ignored and mapped, by source column.
        columnChanges: {},
        serverErrors: {},
        // Rows whose field errors show: edited ones, all after "Continua".
        touchedKeys: [],
        showAllErrors: false,
        // Search and page each row was last edited on, to bring it back.
        locations: {},
        searchField: SEARCH_FIELDS.COLUMN,
        searchText: '',
        search: {},
        searchCount: 0,
        page: 1,
        isLoadingFile: false,
        isLoadingOther: false,
        otherRequestCount: 0,
        isSaving: false,
      };
    },
    computed: {
      fileColumns() {
        return this.columns.map(({ sourceText }) => sourceText);
      },
      arrangement() {
        return {
          drafts: this.drafts,
          removedIds: this.removed.map(({ id }) => id),
          columnChanges: this.columnChanges,
        };
      },
      // Groups 1 and 2, in full and in file order.
      localRows() {
        const ownRows = this.drafts.filter(({ afterKey }) => afterKey === null);
        const rows = [
          ...sortByFileColumn(ownRows, this.fileColumns),
          ...sortByFileColumn(this.fileRows, this.fileColumns).map(
            this.withEdits
          ),
        ];
        return this.visible(rows);
      },
      pageWindow() {
        return pageWindow(this.page, this.localRows.length);
      },
      // Group 3: none when entered from the summary, or when the analysis
      // search found no analysis.
      hasOtherRows() {
        return (
          this.mode === COLUMNS_MODES.ALL &&
          !(this.search.analysisIds && this.search.analysisIds.length === 0)
        );
      },
      otherWhere() {
        return this.hasOtherRows
          ? otherRowsWhere(this.fileColumns, this.search)
          : null;
      },
      otherFilter() {
        if (
          !this.hasFileRows ||
          !this.otherWhere ||
          this.pageWindow.limit === 0
        ) {
          return null;
        }
        return otherRowsFilter(this.otherWhere, this.pageWindow);
      },
      // Watched as strings: a recomputed but equal query sends nothing.
      otherWhereKey() {
        return JSON.stringify(this.otherWhere);
      },
      otherFilterKey() {
        return JSON.stringify(this.otherFilter);
      },
      pageRows() {
        const { localStart, localEnd } = this.pageWindow;
        const otherRows = this.otherFilter
          ? this.visible(this.otherRows.map(this.withEdits))
          : [];
        return [...this.localRows.slice(localStart, localEnd), ...otherRows];
      },
      total() {
        return this.localRows.length + (this.otherWhere ? this.otherCount : 0);
      },
      writes() {
        return pendingWrites({
          drafts: this.drafts,
          edits: this.edits,
          removed: this.removed,
          columnChanges: this.columnChanges,
        });
      },
      writeCount() {
        return countWrites(this.writes);
      },
      // Said, not blocking: their values are just not imported (C46, C59).
      leftUnmapped() {
        return columnsLeftUnmapped(
          this.columns,
          this.writes,
          this.removed.map(({ id }) => id)
        );
      },
      rowsToWrite() {
        return [
          ...this.writes.patches.map(({ row }) => row),
          ...this.writes.creates,
        ];
      },
      pendingKeys() {
        return this.rowsToWrite.map(({ key }) => key);
      },
      fieldErrors() {
        return this.rowsToWrite
          .filter(
            ({ key }) => this.showAllErrors || this.touchedKeys.includes(key)
          )
          .reduce((errors, row) => {
            const messages = Object.entries(rowErrors(row)).reduce(
              (byField, [field, code]) => ({
                ...byField,
                [field]: this.$t(`${this.I18N}.errors.${code}`, {
                  max: MAX_DECIMALS,
                }),
              }),
              {}
            );
            return { ...errors, [row.key]: messages };
          }, {});
      },
      isLoading() {
        return this.isLoadingFile || this.isLoadingOther;
      },
    },
    watch: {
      // Writes waiting for "Continua" are lost going back (C58).
      writeCount: {
        handler(count) {
          this.$emit('dirty', count > 0);
        },
        immediate: true,
      },
      otherWhereKey: { handler: 'loadOtherCount', immediate: true },
      otherFilterKey: { handler: 'loadOtherRows', immediate: true },
    },
    created() {
      this.resolveSearchLater = debounce(this.resolveSearch, SEARCH_DELAY_MS);
      this.loadFileRows();
    },
    beforeDestroy() {
      this.resolveSearchLater.cancel();
    },
    methods: {
      withEdits(row) {
        const edit = this.edits[row.id];
        return edit ? edit.row : row;
      },
      visible(rows) {
        return arrangeRows(rows, this.arrangement).filter((row) =>
          matchesSearch(row, this.search)
        );
      },

      // ── Reading ───────────────────────────────────────────────────────

      async loadFileRows() {
        if (this.fileColumns.length === 0) {
          this.hasFileRows = true;
          return;
        }
        this.isLoadingFile = true;
        try {
          const { data } = await analysisImportMappingApi.index(
            fileRowsFilter(this.fileColumns)
          );
          this.fileRows = data.map(savedRow);
        } catch (error) {
          this.notifyError(serverErrorMessage(error));
        } finally {
          this.isLoadingFile = false;
          this.hasFileRows = true;
        }
      },
      async loadOtherRows() {
        this.otherRequestCount += 1;
        const current = this.otherRequestCount;
        if (!this.otherFilter) {
          this.otherRows = [];
          return;
        }
        this.isLoadingOther = true;
        try {
          const { data } = await analysisImportMappingApi.index(
            this.otherFilter
          );
          // A slower earlier page never replaces a later one.
          if (current === this.otherRequestCount) {
            this.otherRows = data.map(savedRow);
          }
        } catch (error) {
          this.notifyError(serverErrorMessage(error));
        } finally {
          if (current === this.otherRequestCount) this.isLoadingOther = false;
        }
      },
      async loadOtherCount() {
        if (!this.otherWhere) {
          this.otherCount = 0;
          return;
        }
        const where = this.otherWhere;
        try {
          const { data } = await analysisImportMappingApi.count(where);
          if (where === this.otherWhere) this.otherCount = data.count;
        } catch (error) {
          this.notifyError(serverErrorMessage(error));
        }
      },
      reload() {
        return Promise.all([
          this.loadFileRows(),
          this.loadOtherRows(),
          this.loadOtherCount(),
        ]);
      },

      // ── Search ────────────────────────────────────────────────────────

      setSearchText(text) {
        this.searchText = text;
        this.resolveSearchLater();
      },
      setSearchField(field) {
        this.searchField = field;
        this.resolveSearchLater();
      },
      // By analysis, the ids come first from the analyses the user can see
      // (C24); the rows are then filtered by them.
      async resolveSearch() {
        this.searchCount += 1;
        const current = this.searchCount;
        const text = this.searchText.trim();
        let search = {};
        if (text && this.searchField === SEARCH_FIELDS.COLUMN) {
          search = { column: text };
        } else if (text) {
          try {
            const analyses = await searchAnalyses(text);
            search = { analysisIds: analyses.map(({ id }) => id) };
          } catch (error) {
            this.notifyError(serverErrorMessage(error));
            return;
          }
        }
        if (current !== this.searchCount) return;
        this.search = search;
        this.page = 1;
      },

      // ── Editing ───────────────────────────────────────────────────────

      nextDraftKey() {
        this.draftCount += 1;
        return `draft-${this.draftCount}`;
      },
      remember(key) {
        this.$set(this.locations, key, {
          searchField: this.searchField,
          searchText: this.searchText,
          search: this.search,
          page: this.page,
        });
        if (!this.touchedKeys.includes(key)) {
          this.touchedKeys = [...this.touchedKeys, key];
        }
      },
      handleChange({ row, changes }) {
        if (row.id === null) {
          this.drafts = this.drafts.map((draft) =>
            draft.key === row.key ? { ...draft, ...changes } : draft
          );
        } else {
          const edit = this.edits[row.id];
          this.$set(this.edits, row.id, {
            saved: edit ? edit.saved : row,
            row: { ...row, ...changes },
          });
        }
        this.$delete(this.serverErrors, row.key);
        this.remember(row.key);
      },
      handleAction({ action, row }) {
        const handlers = {
          [ROW_ACTIONS.DUPLICATE]: this.duplicate,
          [ROW_ACTIONS.IGNORE]: this.ignore,
          [ROW_ACTIONS.MAP]: this.map,
          [ROW_ACTIONS.DEACTIVATE]: this.deactivate,
          [ROW_ACTIONS.REMOVE]: this.remove,
          [ROW_ACTIONS.UNDO]: this.undo,
        };
        handlers[action](row);
      },
      // The duplicate shows right below its row (C45).
      duplicate(row) {
        const draft = draftRow(this.nextDraftKey(), {
          sourceColumn: row.sourceColumn,
          afterKey: row.key,
          required: true,
          fields: duplicateFields(row),
        });
        this.drafts = [draft, ...this.drafts];
        this.remember(draft.key);
      },
      ignore({ sourceColumn }) {
        this.$set(this.columnChanges, sourceColumn, { ignored: true });
        this.remember(pendingIgnoredKey(sourceColumn));
      },
      map(row) {
        const draft = draftRow(this.nextDraftKey(), {
          sourceColumn: row.sourceColumn,
          afterKey: row.key,
          required: true,
        });
        this.drafts = [draft, ...this.drafts];
        this.$set(this.columnChanges, row.sourceColumn, {
          ignored: false,
          ignoredRowId: row.id,
          anchorKey: draft.key,
        });
        this.remember(draft.key);
      },
      deactivate({ id, key, sourceColumn }) {
        this.removed = [...this.removed, { id, key, sourceColumn }];
        this.$delete(this.edits, id);
      },
      // Its duplicates take its place.
      remove({ key, afterKey }) {
        this.reparent(key, afterKey);
        this.drafts = this.drafts.filter((draft) => draft.key !== key);
      },
      undo({ sourceColumn }) {
        const change = this.columnChanges[sourceColumn];
        if (!change.ignored) this.dropDraftTree(change.anchorKey);
        this.$delete(this.columnChanges, sourceColumn);
      },
      reparent(fromKey, toKey) {
        this.drafts = this.drafts.map((draft) =>
          draft.afterKey === fromKey ? { ...draft, afterKey: toKey } : draft
        );
      },
      dropDraftTree(key) {
        const keys = [key];
        let added = true;
        while (added) {
          const children = this.drafts
            .filter(({ afterKey }) => keys.includes(afterKey))
            .map((draft) => draft.key)
            .filter((childKey) => !keys.includes(childKey));
          keys.push(...children);
          added = children.length > 0;
        }
        this.drafts = this.drafts.filter((draft) => !keys.includes(draft.key));
      },

      // ── Writing ───────────────────────────────────────────────────────

      async proceed() {
        if (this.isSaving) return;
        this.showAllErrors = true;
        const invalid = invalidRows(this.writes);
        if (invalid.length > 0) {
          this.notifyError(
            this.$tc(`${this.I18N}.invalidRows`, invalid.length)
          );
          this.reveal(invalid[0]);
          return;
        }
        this.isSaving = true;
        this.serverErrors = {};
        try {
          const failures = await this.write(buildOperations(this.writes));
          if (failures > 0) {
            this.notifyError(this.$tc(`${this.I18N}.writeFailed`, failures));
            await this.reload();
            return;
          }
          const { data } = await analysesDataApi.prepareImport(this.input);
          this.$emit('prepared', data);
        } catch (error) {
          this.notifyError(serverErrorMessage(error));
        } finally {
          this.isSaving = false;
        }
      },
      // Back to the search and page the row was edited on.
      reveal({ key }) {
        const location = this.locations[key];
        if (!location || this.pageRows.some((row) => row.key === key)) return;
        this.resolveSearchLater.cancel();
        this.searchCount += 1;
        Object.assign(this, location);
      },
      // In order; after a failure, the later writes of the same column are
      // skipped. Resolves with the number of failures.
      write(operations) {
        const failedColumns = [];
        return operations.reduce(
          (previous, operation) =>
            previous.then(async (failures) => {
              if (failedColumns.includes(operation.sourceColumn)) {
                return failures;
              }
              try {
                await this.run(operation);
                return failures;
              } catch (error) {
                failedColumns.push(operation.sourceColumn);
                this.fail(operation, error);
                return failures + 1;
              }
            }),
          Promise.resolve(0)
        );
      },
      // Every successful write is dropped from the state, so a new attempt
      // does not repeat it.
      async run(operation) {
        const { type, id, data, sourceColumn } = operation;
        if (type === OPERATIONS.DELETE) {
          await analysisImportMappingApi.delete(id);
          this.applyDeleted(operation);
        } else if (type === OPERATIONS.PATCH) {
          await analysisImportMappingApi.patch(id, data);
          this.$delete(this.edits, id);
        } else if (type === OPERATIONS.DEACTIVATE_COLUMN) {
          await this.deactivateColumn(sourceColumn);
        } else {
          const response = await analysisImportMappingApi.create(data);
          this.applyCreated(operation, response.data);
        }
      },
      applyDeleted({ id, sourceColumn }) {
        this.removed = this.removed.filter((row) => row.id !== id);
        this.$delete(this.edits, id);
        // The drafts shown after it become rows of their own.
        this.reparent(savedKey(id), null);
        const change = this.columnChanges[sourceColumn];
        if (change && change.ignoredRowId === id) {
          this.$delete(this.columnChanges, sourceColumn);
        }
      },
      // Every active row of the column, on any page (C46).
      async deactivateColumn(sourceColumn) {
        const { data } = await analysisImportMappingApi.index({
          where: { active: true, sourceColumn },
        });
        await data.reduce(
          (previous, { id }) =>
            previous.then(async () => {
              await analysisImportMappingApi.delete(id);
              this.applyDeleted({ id, sourceColumn });
            }),
          Promise.resolve()
        );
        // The column keeps a row of its own, so its pending state stays shown.
        const hasOwnRow = this.drafts.some(
          (draft) =>
            draft.sourceColumn === sourceColumn && draft.afterKey === null
        );
        if (!hasOwnRow) {
          this.drafts = [
            ...this.drafts,
            draftRow(this.nextDraftKey(), { sourceColumn }),
          ];
        }
      },
      applyCreated({ key, sourceColumn }, mapping) {
        if (key === pendingIgnoredKey(sourceColumn)) {
          // Ignored now: its drafts could no longer be written.
          this.$delete(this.columnChanges, sourceColumn);
          this.drafts = this.drafts.filter(
            (draft) => draft.sourceColumn !== sourceColumn
          );
          return;
        }
        this.reparent(key, savedKey(mapping.id));
        this.drafts = this.drafts.filter((draft) => draft.key !== key);
      },
      fail(operation, error) {
        this.$set(this.serverErrors, operation.key, serverErrorMessage(error));
        // A row that could not be deactivated shows again, with its message.
        if (operation.type === OPERATIONS.DELETE) {
          this.removed = this.removed.filter(({ id }) => id !== operation.id);
        }
      },
      notifyError(message) {
        this.$notify({ showClose: true, type: 'error', message });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .columns-step__search {
    margin-bottom: 1rem;
  }

  .columns-step__search-field {
    width: 8rem;
  }

  .columns-step__pagination {
    margin-top: 1rem;
    text-align: right;
  }

  .columns-step__actions {
    gap: 1rem;
    margin-top: 1rem;
  }

  .columns-step__secondary {
    color: $--color-text-regular;
  }
</style>
